import React, { useCallback, useDeferredValue, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IntelligentSearchModal } from "../components/IntelligentSearchModal";
import { MultiSelectChipGroup } from "../components/MultiSelectChipGroup";
import { MessageBox, useMessageBox } from "../components/MessageBox";
import { useSubscriptionContext } from "../contexts/SubscriptionContext";
import {
  buildJobSearchContext,
  createDefaultJobSearchFilters,
  createJobSearchBoards,
  getJobSearchBoardByDomain,
  getJobSearchSourceIdsForDomains,
  getSelectedJobSearchSourceIds,
  JOB_SEARCH_BOARD_CATALOG,
  JOB_SEARCH_JOB_TYPE_OPTIONS,
  JOB_SEARCH_SENIORITY_OPTIONS,
  JOB_SEARCH_TIER_NAMES,
  type JobSearchBoard,
  type JobSearchFilters,
} from "../features/jobSearch/config";
import {
  getJobImportActionLabel,
  useSearchResultApplicationImport,
} from "../hooks/useSearchResultApplicationImport";
import {
  createAgent,
  deleteAgent,
  estimateTokens,
  listAgents,
  listDiscoveredJobs,
  listRuns,
  runAgent,
  updateAgent,
  type DiscoveredJob,
  type JobSearchAgent,
  type JobSearchAgentEstimate,
  type JobSearchAgentMutation,
  type JobSearchAgentRun,
} from "../services/jobSearchAgentService";
import type { IntelligentSearchResult } from "../services/intelligentSearchService";
import "./AgentsPage.css";

const TERMINAL_STATUSES = new Set([
  "completed",
  "completed_with_errors",
  "failed",
  "skipped_empty",
]);
const LEGACY_SOURCE_LIMIT = 20;
const AGENTS_PER_PAGE = 3;

type AgentSortOption =
  | "default"
  | "name-asc"
  | "name-desc"
  | "next-run-soonest"
  | "last-run-recent";

type AgentDrawerTab = "results" | "runs";
type SourcePerformanceSummary = {
  productiveSites: Array<{
    domain: string;
    label: string;
    sourceId: string | null;
    resultCount: number;
  }>;
  emptySites: Array<{
    domain: string;
    label: string;
    sourceId: string | null;
  }>;
};

const formatTimestamp = (value?: number | null): string => {
  if (!value) {
    return "Not scheduled";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const describeRunStatus = (status?: string | null): string => {
  switch (status) {
    case "queued":
      return "Queued";
    case "processing":
      return "Running";
    case "completed":
      return "Completed";
    case "completed_with_errors":
      return "Completed with errors";
    case "failed":
      return "Failed";
    case "skipped_empty":
      return "Skipped";
    default:
      return "Idle";
  }
};

const estimateText = (estimate: JobSearchAgentEstimate | null): string => {
  if (!estimate) {
    return "Select at least one source to estimate tokens.";
  }
  return `${estimate.tokensPerRun} tokens per run across ${estimate.sourceCount} sources`;
};

const createEmptyDraft = () => ({
  name: "",
  filters: createDefaultJobSearchFilters(),
  boards: createJobSearchBoards(),
  intervalWeeks: 1,
  isActive: true,
});

const createDraftFromAgent = (agent: JobSearchAgent) => ({
  name: agent.name,
  filters: agent.filters ?? createDefaultJobSearchFilters(),
  boards: createJobSearchBoards(
    agent.source_ids?.length > 0
      ? agent.source_ids
      : getJobSearchSourceIdsForDomains(agent.sites ?? []),
  ),
  intervalWeeks: agent.interval_weeks,
  isActive: agent.is_active,
});

type GroupedRunSiteSummaries = {
  errorGroups: Array<{
    message: string;
    domains: string[];
  }>;
  successSites: JobSearchAgentRun["site_summaries"];
};

const groupRunSiteSummaries = (
  siteSummaries: JobSearchAgentRun["site_summaries"],
): GroupedRunSiteSummaries => {
  const errorGroups = new Map<string, string[]>();
  const successSites: JobSearchAgentRun["site_summaries"] = [];

  siteSummaries.forEach((site) => {
    if (site.error) {
      const domains = errorGroups.get(site.error) ?? [];
      domains.push(site.domain);
      errorGroups.set(site.error, domains);
      return;
    }
    successSites.push(site);
  });

  return {
    errorGroups: Array.from(errorGroups.entries()).map(([message, domains]) => ({
      message,
      domains,
    })),
    successSites,
  };
};

const buildDiscoveredJobsReviewData = (
  agent: JobSearchAgent | null,
  jobs: DiscoveredJob[],
): IntelligentSearchResult | null => {
  if (!agent || jobs.length === 0) {
    return null;
  }

  return {
    results: jobs.map((job) => ({
      title: job.title || "Untitled job",
      snippet: job.snippet || "No description available",
      url: job.canonical_url,
      fullContent: job.full_content?.trim() || job.snippet || undefined,
    })),
    citations: [],
    summary:
      "Review discovered jobs and add the ones you want to your job applications list using the same workflow as Intelligent Search.",
    searchQuery: agent.name,
  };
};

const summarizeSourcePerformance = (
  siteSummaries: JobSearchAgentRun["site_summaries"],
): SourcePerformanceSummary => {
  const productiveSites: SourcePerformanceSummary["productiveSites"] = [];
  const emptySites: SourcePerformanceSummary["emptySites"] = [];

  siteSummaries.forEach((site) => {
    if (site.error) {
      return;
    }

    const board = getJobSearchBoardByDomain(site.domain);
    const label = board?.name ?? site.domain;
    const sourceId = board?.id ?? null;
    const resultCount = Number(site.resultCount ?? 0);

    if (resultCount > 0) {
      productiveSites.push({
        domain: site.domain,
        label,
        sourceId,
        resultCount,
      });
      return;
    }

    emptySites.push({
      domain: site.domain,
      label,
      sourceId,
    });
  });

  return {
    productiveSites,
    emptySites,
  };
};

const compareNullableNumbers = (
  left: number | null | undefined,
  right: number | null | undefined,
  direction: "asc" | "desc",
): number => {
  const safeLeft = typeof left === "number" ? left : null;
  const safeRight = typeof right === "number" ? right : null;

  if (safeLeft === null && safeRight === null) {
    return 0;
  }
  if (safeLeft === null) {
    return 1;
  }
  if (safeRight === null) {
    return -1;
  }

  return direction === "asc" ? safeLeft - safeRight : safeRight - safeLeft;
};

interface AgentSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}

const AgentSidePanel: React.FC<AgentSidePanelProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  headerActions,
  children,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen || !isMounted) {
      return;
    }

    const shouldLockBodyScroll =
      typeof window !== "undefined" && window.innerWidth <= 768;
    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    if (shouldLockBodyScroll) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isMounted, isOpen, onClose]);

  if (!isOpen || !isMounted || typeof document === "undefined" || !document.body) {
    return null;
  }

  return createPortal(
    <div
      className="agents-drawer-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <aside
        className="agents-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="agents-drawer__header">
          <div className="agents-drawer__title">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {headerActions && (
            <div className="agents-drawer__header-actions">{headerActions}</div>
          )}
          <button
            type="button"
            className="agents-drawer__close"
            onClick={onClose}
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>
        <div className="agents-drawer__content">{children}</div>
      </aside>
    </div>,
    document.body,
  );
};

export const AgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<JobSearchAgent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [runs, setRuns] = useState<JobSearchAgentRun[]>([]);
  const [discoveredJobs, setDiscoveredJobs] = useState<DiscoveredJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftFilters, setDraftFilters] = useState<JobSearchFilters>(
    createDefaultJobSearchFilters,
  );
  const [draftBoards, setDraftBoards] =
    useState<JobSearchBoard[]>(createJobSearchBoards);
  const [draftIntervalWeeks, setDraftIntervalWeeks] = useState(1);
  const [draftIsActive, setDraftIsActive] = useState(true);
  const [estimate, setEstimate] = useState<JobSearchAgentEstimate | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [showDiscoveredJobsReview, setShowDiscoveredJobsReview] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [detailPanel, setDetailPanel] = useState<{
    agentId: string;
    tab: AgentDrawerTab;
  } | null>(null);
  const [agentSearch, setAgentSearch] = useState("");
  const [agentSort, setAgentSort] = useState<AgentSortOption>("default");
  const [currentPage, setCurrentPage] = useState(1);
  const [pollingRun, setPollingRun] = useState<{
    agentId: string;
    runId: string;
  } | null>(null);
  const { messageBox, showMessageBox } = useMessageBox();
  const { reloadSubscription } = useSubscriptionContext();
  const syncedSubscriptionRunIdRef = React.useRef<string | null>(null);
  const deferredAgentSearch = useDeferredValue(agentSearch);

  const resetDraft = useCallback(() => {
    const nextDraft = createEmptyDraft();
    setEditingAgentId(null);
    setDraftName(nextDraft.name);
    setDraftFilters(nextDraft.filters);
    setDraftBoards(nextDraft.boards);
    setDraftIntervalWeeks(nextDraft.intervalWeeks);
    setDraftIsActive(nextDraft.isActive);
  }, []);

  const closeComposer = useCallback(() => {
    setComposerOpen(false);
    resetDraft();
  }, [resetDraft]);

  const loadAgentsData = useCallback(
    async (preferredAgentId?: string | null) => {
      const nextAgents = await listAgents();
      setAgents(nextAgents);
      setSelectedAgentId((current) => {
        const desiredId = preferredAgentId ?? current;
        if (desiredId && nextAgents.some((agent) => agent.agent_id === desiredId)) {
          return desiredId;
        }
        return nextAgents[0]?.agent_id ?? null;
      });
      if (nextAgents.length === 0) {
        setRuns([]);
        setDiscoveredJobs([]);
      }
      return nextAgents;
    },
    [],
  );

  const loadAgentDetails = useCallback(async (agentId: string) => {
    setDetailsLoading(true);
    try {
      const [nextRuns, nextDiscoveredJobs] = await Promise.all([
        listRuns(agentId),
        listDiscoveredJobs(agentId),
      ]);
      setRuns(nextRuns);
      setDiscoveredJobs(nextDiscoveredJobs);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const nextAgents = await listAgents();
        if (cancelled) {
          return;
        }
        setAgents(nextAgents);
        setSelectedAgentId(nextAgents[0]?.agent_id ?? null);
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Failed to load job search agents",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedAgentId) {
      setRuns([]);
      setDiscoveredJobs([]);
      return;
    }

    let cancelled = false;
    setRuns([]);
    setDiscoveredJobs([]);

    void (async () => {
      setDetailsLoading(true);
      try {
        const [nextRuns, nextDiscoveredJobs] = await Promise.all([
          listRuns(selectedAgentId),
          listDiscoveredJobs(selectedAgentId),
        ]);
        if (!cancelled) {
          setRuns(nextRuns);
          setDiscoveredJobs(nextDiscoveredJobs);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Failed to load agent details",
          );
        }
      } finally {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedAgentId]);

  const selectedSourceIds = getSelectedJobSearchSourceIds(draftBoards);
  const selectedSourceIdsKey = selectedSourceIds.join("|");
  const selectedAgent =
    agents.find((agent) => agent.agent_id === selectedAgentId) ?? null;
  const editingAgent =
    agents.find((agent) => agent.agent_id === editingAgentId) ?? null;
  const detailPanelAgent =
    detailPanel && selectedAgent?.agent_id === detailPanel.agentId
      ? selectedAgent
      : agents.find((agent) => agent.agent_id === detailPanel?.agentId) ?? null;
  const latestRunWithSiteSummaries =
    editingAgentId && editingAgent?.agent_id === selectedAgentId
      ? runs.find(
          (run) =>
            TERMINAL_STATUSES.has(run.status) && run.site_summaries.length > 0,
        ) ?? null
      : null;
  const latestSourcePerformance = latestRunWithSiteSummaries
    ? summarizeSourcePerformance(latestRunWithSiteSummaries.site_summaries)
    : null;
  const latestSourcePerformanceHasSignal = Boolean(
    latestSourcePerformance &&
      (latestSourcePerformance.productiveSites.length > 0 ||
        latestSourcePerformance.emptySites.length > 0),
  );
  const noDataSourceIds = new Set(
    latestSourcePerformance?.emptySites
      .map((site) => site.sourceId)
      .filter((siteId): siteId is string => Boolean(siteId)) ?? [],
  );
  const showsLegacySourceLimitNote = Boolean(
    editingAgent &&
      editingAgent.source_ids.length === LEGACY_SOURCE_LIMIT &&
      JOB_SEARCH_BOARD_CATALOG.length > LEGACY_SOURCE_LIMIT,
  );
  const discoveredJobsReviewData = buildDiscoveredJobsReviewData(
    selectedAgent,
    discoveredJobs,
  );
  const discoveredJobImportResults = React.useMemo(
    () =>
      discoveredJobs.map((job) => ({
        title: job.title || "Untitled job",
        snippet: job.snippet || "No description available",
        url: job.canonical_url,
        fullContent: job.full_content?.trim() || job.snippet || undefined,
      })),
    [discoveredJobs],
  );
  const {
    saveStatusByUrl: discoveredJobSaveStatusByUrl,
    saveErrorByUrl: discoveredJobSaveErrorByUrl,
    handleAddToApplications: handleAddDiscoveredJobToApplications,
  } = useSearchResultApplicationImport({
    isEnabled: detailPanel?.tab === "results",
    results: discoveredJobImportResults,
  });
  const formIsValid =
    draftName.trim().length > 0 &&
    draftFilters.keywords.trim().length > 0 &&
    selectedSourceIds.length > 0;
  const normalizedAgentSearch = deferredAgentSearch.trim().toLowerCase();
  const filteredAgents = React.useMemo(() => {
    const matchingAgents = normalizedAgentSearch
      ? agents.filter((agent) =>
          agent.name.toLowerCase().includes(normalizedAgentSearch),
        )
      : agents;

    if (agentSort === "default") {
      return matchingAgents;
    }

    const nextAgents = [...matchingAgents];
    nextAgents.sort((left, right) => {
      switch (agentSort) {
        case "name-asc":
          return left.name.localeCompare(right.name);
        case "name-desc":
          return right.name.localeCompare(left.name);
        case "next-run-soonest":
          return compareNullableNumbers(left.next_run_at, right.next_run_at, "asc");
        case "last-run-recent":
          return compareNullableNumbers(left.last_run_at, right.last_run_at, "desc");
        default:
          return 0;
      }
    });

    return nextAgents;
  }, [agentSort, agents, normalizedAgentSearch]);
  const totalPages = Math.max(1, Math.ceil(filteredAgents.length / AGENTS_PER_PAGE));
  const startIndex = filteredAgents.length === 0 ? 0 : (currentPage - 1) * AGENTS_PER_PAGE;
  const endIndex = Math.min(startIndex + AGENTS_PER_PAGE, filteredAgents.length);
  const paginatedAgents = filteredAgents.slice(startIndex, endIndex);
  const hasAgentSearch = normalizedAgentSearch.length > 0;

  useEffect(() => {
    if (detailPanel && !agents.some((agent) => agent.agent_id === detailPanel.agentId)) {
      setDetailPanel(null);
    }
  }, [agents, detailPanel]);

  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedAgentSearch, agentSort]);

  useEffect(() => {
    setCurrentPage((previousPage) => Math.min(previousPage, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setShowDiscoveredJobsReview(false);
  }, [selectedAgentId]);

  useEffect(() => {
    const sourceIds = selectedSourceIdsKey
      ? selectedSourceIdsKey.split("|").filter(Boolean)
      : [];

    if (sourceIds.length === 0 || draftFilters.keywords.trim().length === 0) {
      setEstimate(null);
      setEstimateLoading(false);
      return;
    }

    let cancelled = false;
    setEstimateLoading(true);
    const timer = window.setTimeout(() => {
      void estimateTokens({ sourceIds })
        .then((nextEstimate) => {
          if (!cancelled) {
            setEstimate(nextEstimate);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setEstimate(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setEstimateLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [draftFilters.keywords, selectedSourceIdsKey]);

  useEffect(() => {
    if (!pollingRun) {
      return;
    }

    let cancelled = false;
    let requestInFlight = false;

    const pollRunStatus = async () => {
      if (cancelled || requestInFlight) {
        return;
      }

      requestInFlight = true;
      try {
        const nextRuns = await listRuns(pollingRun.agentId);
        if (cancelled) {
          return;
        }

        if (selectedAgentId === pollingRun.agentId) {
          setRuns(nextRuns);
        }

        const currentRun = nextRuns.find(
          (run) => run.run_id === pollingRun.runId,
        );
        if (!currentRun || !TERMINAL_STATUSES.has(currentRun.status)) {
          return;
        }

        const shouldSyncSubscription =
          syncedSubscriptionRunIdRef.current !== currentRun.run_id;

        setPollingRun(null);
        await loadAgentsData(pollingRun.agentId);
        if (cancelled) {
          return;
        }
        if (selectedAgentId === pollingRun.agentId) {
          await loadAgentDetails(pollingRun.agentId);
        }
        if (cancelled) {
          return;
        }
        if (shouldSyncSubscription) {
          syncedSubscriptionRunIdRef.current = currentRun.run_id;
          await reloadSubscription(false, false, true).catch(() => undefined);
        }
      } catch (pollError) {
        if (!cancelled) {
          setPollingRun(null);
          setError(
            pollError instanceof Error
              ? pollError.message
              : "Failed to refresh agent run status",
          );
        }
      } finally {
        requestInFlight = false;
      }
    };

    void pollRunStatus();
    const intervalId = window.setInterval(() => {
      void pollRunStatus();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    loadAgentDetails,
    loadAgentsData,
    pollingRun,
    reloadSubscription,
    selectedAgentId,
  ]);

  const handleDraftFilterChange = <K extends keyof JobSearchFilters>(
    field: K,
    value: JobSearchFilters[K],
  ) => {
    setDraftFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const toggleDraftBoard = (boardId: string) => {
    setDraftBoards((current) =>
      current.map((board) =>
        board.id === boardId ? { ...board, selected: !board.selected } : board,
      ),
    );
  };

  const selectAllBoards = () => {
    setDraftBoards((current) =>
      current.map((board) => ({ ...board, selected: true })),
    );
  };

  const clearBoardSelection = () => {
    setDraftBoards((current) =>
      current.map((board) => ({ ...board, selected: false })),
    );
  };

  const handleDeselectNoDataSources = () => {
    if (noDataSourceIds.size === 0) {
      return;
    }

    setDraftBoards((current) =>
      current.map((board) =>
        noDataSourceIds.has(board.id) ? { ...board, selected: false } : board,
      ),
    );
  };

  const openCreateComposer = () => {
    resetDraft();
    setDetailPanel(null);
    setShowDiscoveredJobsReview(false);
    setComposerOpen(true);
    setError(null);
  };

  const startEditing = (agent: JobSearchAgent) => {
    const nextDraft = createDraftFromAgent(agent);
    setEditingAgentId(agent.agent_id);
    setDraftName(nextDraft.name);
    setDraftFilters(nextDraft.filters);
    setDraftBoards(nextDraft.boards);
    setDraftIntervalWeeks(nextDraft.intervalWeeks);
    setDraftIsActive(nextDraft.isActive);
    setSelectedAgentId(agent.agent_id);
    setDetailPanel(null);
    setShowDiscoveredJobsReview(false);
    setComposerOpen(true);
    setError(null);
  };

  const openDetailPanel = (agent: JobSearchAgent, tab: AgentDrawerTab) => {
    setSelectedAgentId(agent.agent_id);
    setComposerOpen(false);
    setDetailPanel({ agentId: agent.agent_id, tab });
    setError(null);
  };

  const buildMutationPayload = (): JobSearchAgentMutation => ({
    name: draftName.trim(),
    filters: draftFilters,
    sourceIds: selectedSourceIds,
    intervalWeeks: draftIntervalWeeks,
    isActive: draftIsActive,
  });

  const handleSubmit = async () => {
    if (!formIsValid) {
      setError("Add a name, keywords, and at least one source before saving.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildMutationPayload();
      const savedAgent = editingAgentId
        ? await updateAgent(editingAgentId, payload)
        : await createAgent(payload);
      await loadAgentsData(savedAgent.agent_id);
      await loadAgentDetails(savedAgent.agent_id);
      setSelectedAgentId(savedAgent.agent_id);
      setComposerOpen(false);
      resetDraft();
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save agent",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (agent: JobSearchAgent) => {
    setError(null);
    try {
      await updateAgent(agent.agent_id, { isActive: !agent.is_active });
      await loadAgentsData(agent.agent_id);
    } catch (toggleError) {
      setError(
        toggleError instanceof Error
          ? toggleError.message
          : "Failed to update agent",
      );
    }
  };

  const handleDelete = (agent: JobSearchAgent) => {
    showMessageBox({
      type: "confirm",
      title: "Delete job search agent?",
      message: `Remove "${agent.name}" and its current results/run history from agent management.`,
      description:
        "Future reruns will rebuild results from fresh search data, so deleting the agent also removes its current result snapshot.",
      buttons: ["cancel", "yes"],
      buttonLabels: { yes: "Delete agent" },
      onButtonClick: (button) => {
        if (button !== "yes") {
          return;
        }
        void (async () => {
          try {
            await deleteAgent(agent.agent_id);
            if (detailPanel?.agentId === agent.agent_id) {
              setDetailPanel(null);
            }
            if (editingAgentId === agent.agent_id) {
              closeComposer();
            }
            const nextAgents = await loadAgentsData();
            if (selectedAgentId === agent.agent_id && nextAgents.length === 0) {
              resetDraft();
            }
          } catch (deleteError) {
            setError(
              deleteError instanceof Error
                ? deleteError.message
                : "Failed to delete agent",
            );
          }
        })();
      },
    });
  };

  const handleRunNow = async (agent: JobSearchAgent) => {
    setError(null);
    let currentEstimate: JobSearchAgentEstimate | null = null;
    try {
      currentEstimate = await estimateTokens({
        sourceIds: agent.source_ids,
        sites: agent.sites,
      });
    } catch {
      currentEstimate = null;
    }

    const description = currentEstimate
      ? `${currentEstimate.tokensPerRun} tokens estimated for this run at ${currentEstimate.tokenCostPerSearch} tokens per source.`
      : "Token estimate is unavailable, but this run will still be charged per source searched.";

    showMessageBox({
      type: "confirm",
      title: "Run agent now?",
      message: `Queue a manual run for "${agent.name}" without changing its scheduled cadence.`,
      description,
      buttons: ["cancel", "yes"],
      buttonLabels: { yes: "Run now" },
      onButtonClick: (button) => {
        if (button !== "yes") {
          return;
        }
        void (async () => {
          try {
            const run = await runAgent(agent.agent_id);
            syncedSubscriptionRunIdRef.current = null;
            setSelectedAgentId(agent.agent_id);
            setPollingRun({ agentId: agent.agent_id, runId: run.run_id });
            await loadAgentsData(agent.agent_id);
            await loadAgentDetails(agent.agent_id);
          } catch (runError) {
            setError(
              runError instanceof Error
                ? runError.message
                : "Failed to queue agent run",
            );
          }
        })();
      },
    });
  };

  const handleOpenDiscoveredJobsReview = () => {
    setDetailPanel(null);
    setShowDiscoveredJobsReview(true);
  };

  return (
    <div className="agents-page">
      <MessageBox {...messageBox} />
      <IntelligentSearchModal
        isOpen={showDiscoveredJobsReview}
        onClose={() => setShowDiscoveredJobsReview(false)}
        results={discoveredJobsReviewData}
        isLoading={detailsLoading}
        error={null}
        title="Discovered Jobs"
        resultsLabel="Discovered Jobs"
      />

      <AgentSidePanel
        isOpen={composerOpen}
        onClose={closeComposer}
        title={editingAgentId ? "Edit Agent" : "Create Agent"}
        subtitle="Structured filters, shared board catalog, and live token estimates."
      >
        <div className="agents-form">
          <label className="agents-field">
            <span>Name</span>
            <input
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              placeholder="Remote product roles"
            />
          </label>

          <div className="agents-form__grid">
            <label className="agents-field agents-field--wide">
              <span>Keywords</span>
              <input
                value={draftFilters.keywords}
                onChange={(event) =>
                  handleDraftFilterChange("keywords", event.target.value)
                }
                placeholder="Product manager, growth, lifecycle"
              />
            </label>

            <label className="agents-field">
              <span>Location</span>
              <input
                value={draftFilters.location}
                onChange={(event) =>
                  handleDraftFilterChange("location", event.target.value)
                }
                placeholder="Remote, Denver, New York"
              />
            </label>

            <label className="agents-field">
              <span>Recency</span>
              <select
                value={String(draftFilters.recencyDays ?? 7)}
                onChange={(event) =>
                  handleDraftFilterChange(
                    "recencyDays",
                    Number(event.target.value),
                  )
                }
              >
                <option value="1">Past 24 hours</option>
                <option value="7">Past week</option>
                <option value="30">Past month</option>
                <option value="90">Past 3 months</option>
              </select>
            </label>

            <label className="agents-field">
              <span>Experience</span>
              <select
                value={draftFilters.experience}
                onChange={(event) =>
                  handleDraftFilterChange("experience", event.target.value)
                }
              >
                <option value="">Any experience</option>
                <option value="0-1 years OR entry">0-1 years</option>
                <option value="1-3 years OR 2-4 years">1-3 years</option>
                <option value="3-5 years OR 4-6 years">3-5 years</option>
                <option value="5+ years OR 6+ years">5+ years</option>
                <option value="7+ years OR 8+ years">7+ years</option>
              </select>
            </label>

            <div className="agents-field agents-field--wide">
              <span>Job Type</span>
              <MultiSelectChipGroup
                ariaLabel="Job Type"
                options={JOB_SEARCH_JOB_TYPE_OPTIONS}
                selectedValues={draftFilters.jobType}
                onChange={(value) => handleDraftFilterChange("jobType", value)}
              />
            </div>

            <div className="agents-field agents-field--wide">
              <span>Seniority</span>
              <MultiSelectChipGroup
                ariaLabel="Seniority"
                options={JOB_SEARCH_SENIORITY_OPTIONS}
                selectedValues={draftFilters.seniority}
                onChange={(value) => handleDraftFilterChange("seniority", value)}
              />
            </div>

            <label className="agents-field agents-field--wide">
              <span>Exclude Keywords</span>
              <input
                value={draftFilters.exclusions}
                onChange={(event) =>
                  handleDraftFilterChange("exclusions", event.target.value)
                }
                placeholder="intern, director, healthcare"
              />
            </label>
          </div>

          {(showsLegacySourceLimitNote || latestSourcePerformanceHasSignal) && (
            <div className="agents-source-feedback">
              {showsLegacySourceLimitNote && (
                <section className="agents-source-feedback__card agents-source-feedback__card--warning">
                  <div className="agents-source-feedback__copy">
                    <strong>Saved Before The Full Source Catalog Was Supported</strong>
                    <p>
                      If this agent was saved before the source-limit fix, only the
                      first 20 selected sources were kept. Reselect any missing
                      sources below and save once to preserve the full set.
                    </p>
                  </div>
                </section>
              )}

              {latestRunWithSiteSummaries &&
                latestSourcePerformance &&
                latestSourcePerformanceHasSignal && (
                <section className="agents-source-feedback__card">
                  <div className="agents-source-feedback__header">
                    <div className="agents-source-feedback__copy">
                      <strong>Latest Source Performance</strong>
                      <p>
                        Latest completed run:{" "}
                        {formatTimestamp(
                          latestRunWithSiteSummaries.completed_at ??
                            latestRunWithSiteSummaries.started_at ??
                            latestRunWithSiteSummaries.queued_at,
                        )}
                        . Keep the productive sources selected, and consider
                        deselecting the sources that returned no data for this
                        search criteria.
                      </p>
                    </div>
                    {noDataSourceIds.size > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={handleDeselectNoDataSources}
                      >
                        Deselect No-Data Sites
                      </button>
                    )}
                  </div>

                  {latestSourcePerformance.productiveSites.length > 0 && (
                    <div className="agents-source-feedback__section">
                      <span className="agents-source-feedback__label">
                        Returned jobs
                      </span>
                      <div className="agents-source-feedback__chips">
                        {latestSourcePerformance.productiveSites.map((site) => (
                          <span
                            key={`productive-${site.domain}`}
                            className="agents-source-feedback__chip agents-source-feedback__chip--productive"
                          >
                            {site.label} ({site.resultCount})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {latestSourcePerformance.emptySites.length > 0 && (
                    <div className="agents-source-feedback__section">
                      <span className="agents-source-feedback__label">
                        Returned no data
                      </span>
                      <div className="agents-source-feedback__chips">
                        {latestSourcePerformance.emptySites.map((site) => (
                          <span
                            key={`empty-${site.domain}`}
                            className="agents-source-feedback__chip agents-source-feedback__chip--empty"
                          >
                            {site.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}
            </div>
          )}

          <div className="agents-board-picker">
            <div className="agents-board-picker__header">
              <div>
                <h3>Sources</h3>
                <p>
                  Agents search each selected source independently using
                  intelligent search.
                </p>
              </div>
              <div className="agents-board-picker__actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={selectAllBoards}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={clearBoardSelection}
                >
                  Clear
                </button>
              </div>
            </div>

            {[0, 1, 2, 3, 5].map((tier) => {
              const tierBoards = draftBoards.filter((board) => board.tier === tier);
              if (tierBoards.length === 0) {
                return null;
              }
              return (
                <div key={tier} className="agents-board-tier">
                  <div className="agents-board-tier__title">
                    {JOB_SEARCH_TIER_NAMES[tier]}
                  </div>
                  <div className="agents-board-tier__grid">
                    {tierBoards.map((board) => (
                      <button
                        key={board.id}
                        type="button"
                        className={`agents-board-chip ${
                          board.selected ? "agents-board-chip--selected" : ""
                        }`}
                        onClick={() => toggleDraftBoard(board.id)}
                      >
                        <span className="agents-board-chip__icon">{board.icon}</span>
                        <span className="agents-board-chip__body">
                          <strong>{board.name}</strong>
                          <span>{board.description}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="agents-form__footer">
            <div className="agents-estimate">
              <span className="agents-estimate__label">Run Cost</span>
              <strong>{estimateLoading ? "Calculating..." : estimateText(estimate)}</strong>
              <span className="agents-estimate__sub">
                Context: {buildJobSearchContext(draftFilters) || "No extra filters"}
              </span>
            </div>

            <div className="agents-schedule">
              <label className="agents-field">
                <span>Cadence</span>
                <select
                  value={draftIntervalWeeks}
                  onChange={(event) =>
                    setDraftIntervalWeeks(Number(event.target.value))
                  }
                >
                  <option value={1}>Every week</option>
                  <option value={2}>Every 2 weeks</option>
                  <option value={3}>Every 3 weeks</option>
                  <option value={4}>Every 4 weeks</option>
                </select>
              </label>

              <label className="agents-toggle">
                <input
                  type="checkbox"
                  checked={draftIsActive}
                  onChange={(event) => setDraftIsActive(event.target.checked)}
                />
                <span>Agent is active</span>
              </label>
            </div>
          </div>

          <div className="agents-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleSubmit()}
              disabled={saving || !formIsValid}
            >
              {saving
                ? "Saving..."
                : editingAgentId
                  ? "Save Changes"
                  : "Create Agent"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={resetDraft}
            >
              Reset Form
            </button>
          </div>
        </div>
      </AgentSidePanel>

      <AgentSidePanel
        isOpen={Boolean(detailPanel && detailPanelAgent)}
        onClose={() => setDetailPanel(null)}
        title={
          detailPanel?.tab === "runs" ? "Run History" : "Agent Results"
        }
        subtitle={
          detailPanelAgent
            ? `${detailPanelAgent.name} — ${detailPanelAgent.query}`
            : undefined
        }
        headerActions={
          detailPanelAgent ? (
            <div className="agents-detail-tabs">
              <button
                type="button"
                className={`agents-detail-tabs__button ${
                  detailPanel?.tab === "results"
                    ? "agents-detail-tabs__button--active"
                    : ""
                }`}
                onClick={() =>
                  setDetailPanel({
                    agentId: detailPanelAgent.agent_id,
                    tab: "results",
                  })
                }
              >
                Results
              </button>
              <button
                type="button"
                className={`agents-detail-tabs__button ${
                  detailPanel?.tab === "runs"
                    ? "agents-detail-tabs__button--active"
                    : ""
                }`}
                onClick={() =>
                  setDetailPanel({
                    agentId: detailPanelAgent.agent_id,
                    tab: "runs",
                  })
                }
              >
                Runs
              </button>
            </div>
          ) : null
        }
      >
        {detailPanel?.tab === "runs" ? (
          <div className="agents-detail">
            <div className="agents-detail__toolbar">
              <div>
                <h3>Latest agent activity</h3>
                <p>
                  Review queued, running, and completed searches without leaving
                  agent management.
                </p>
              </div>
            </div>

            {detailsLoading ? (
              <div className="agents-empty">Loading run history...</div>
            ) : runs.length === 0 ? (
              <div className="agents-empty">No runs yet.</div>
            ) : (
              <div className="agents-runs">
                {runs.map((run) => {
                  const groupedSiteSummaries = groupRunSiteSummaries(run.site_summaries);

                  return (
                    <article key={run.run_id} className="agents-run">
                      <div className="agents-run__header">
                        <div>
                          <strong>{describeRunStatus(run.status)}</strong>
                          <span>
                            {run.trigger === "manual" ? "Manual run" : "Scheduled run"}
                          </span>
                        </div>
                        <div className="agents-run__counts">
                          <span>{run.new_jobs_count} new</span>
                          <span>{run.duplicate_skipped_count} duplicates</span>
                        </div>
                      </div>

                      <div className="agents-run__meta">
                        <span>Queued: {formatTimestamp(run.queued_at)}</span>
                        <span>Started: {formatTimestamp(run.started_at)}</span>
                        <span>Completed: {formatTimestamp(run.completed_at)}</span>
                      </div>

                      {groupedSiteSummaries.errorGroups.length > 0 && (
                        <div className="agents-run__error-groups">
                          {groupedSiteSummaries.errorGroups.map((group) => (
                            <div
                              key={`${run.run_id}-${group.message}`}
                              className="agents-run__error-group"
                            >
                              <div className="agents-run__error-header">
                                <strong>{group.message}</strong>
                                <span>{group.domains.length} source(s)</span>
                              </div>
                              <div className="agents-run__error-domains">
                                {group.domains.map((domain) => (
                                  <span
                                    key={`${run.run_id}-${group.message}-${domain}`}
                                    className="agents-run__error-domain"
                                  >
                                    {domain}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {groupedSiteSummaries.successSites.length > 0 && (
                        <div className="agents-run__sites">
                          {groupedSiteSummaries.successSites.map((site) => (
                            <div key={`${run.run_id}-${site.domain}`} className="agents-site-summary">
                              <strong>{site.domain}</strong>
                              <span>
                                {site.newCount ?? 0} new, {site.duplicateCount ?? 0} duplicates,{" "}
                                {site.resultCount ?? 0} results
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {run.error_message && groupedSiteSummaries.errorGroups.length === 0 && (
                        <div className="agents-run__error">{run.error_message}</div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="agents-detail">
            <div className="agents-detail__toolbar">
              <div>
                <h3>Discovered jobs</h3>
                <p>
                  Open the search matches from this agent and review them before
                  adding them to your job applications list.
                </p>
              </div>
              {discoveredJobs.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleOpenDiscoveredJobsReview}
                >
                  Review & Add Jobs
                </button>
              )}
            </div>

            {detailsLoading ? (
              <div className="agents-empty">Loading discovered jobs...</div>
            ) : discoveredJobs.length === 0 ? (
              <div className="agents-empty">
                No discovered jobs yet for this agent.
              </div>
            ) : (
              <div className="agents-discovered">
                {discoveredJobs.map((job) => (
                  <article key={job.discovered_id} className="agents-discovered__item">
                    <div className="agents-discovered__item-main">
                      <a
                        href={job.canonical_url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {job.title || "Untitled job"}
                      </a>
                      <div className="agents-discovered__meta">
                        <span>{job.company || "Unknown company"}</span>
                        <span>{job.location || "Location not captured"}</span>
                        <span>{job.source}</span>
                        <span>{formatTimestamp(job.discovered_at)}</span>
                      </div>
                    </div>
                    {job.snippet && (
                      <p className="agents-discovered__snippet">{job.snippet}</p>
                    )}
                    <div className="agents-discovered__actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() =>
                          handleAddDiscoveredJobToApplications({
                            title: job.title || "Untitled job",
                            snippet: job.snippet || "No description available",
                            url: job.canonical_url,
                            fullContent: job.full_content?.trim() || job.snippet || undefined,
                          })
                        }
                        disabled={
                          !job.canonical_url ||
                          discoveredJobSaveStatusByUrl[job.canonical_url] === "checking" ||
                          discoveredJobSaveStatusByUrl[job.canonical_url] === "saving" ||
                          discoveredJobSaveStatusByUrl[job.canonical_url] === "saved" ||
                          discoveredJobSaveStatusByUrl[job.canonical_url] === "exists"
                        }
                      >
                        {getJobImportActionLabel(
                          discoveredJobSaveStatusByUrl[job.canonical_url] || "idle",
                        )}
                      </button>
                      {discoveredJobSaveStatusByUrl[job.canonical_url] === "saved" && (
                        <span className="agents-discovered__action-status">
                          Added to job applications
                        </span>
                      )}
                      {discoveredJobSaveStatusByUrl[job.canonical_url] === "exists" && (
                        <span className="agents-discovered__action-status">
                          Already added to job applications
                        </span>
                      )}
                      {discoveredJobSaveStatusByUrl[job.canonical_url] === "error" &&
                        discoveredJobSaveErrorByUrl[job.canonical_url] && (
                          <p className="agents-discovered__action-error">
                            {discoveredJobSaveErrorByUrl[job.canonical_url]}
                          </p>
                        )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </AgentSidePanel>

      <header className="agents-page__header" data-tour-id="agents-header">
        <div>
          <h1>Job Search Agents</h1>
          <p>
            Manage scheduled searches from one place, then open results, run
            history, and editing flows in side panels instead of stacked page
            sections.
          </p>
        </div>
        <div className="agents-page__header-actions">
          <div className="agents-page__summary" data-tour-id="agents-summary">
            <span>{agents.length} agents</span>
            <span>{agents.filter((agent) => agent.is_active).length} active</span>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={openCreateComposer}
            data-tour-id="agents-create-button"
          >
            Create Agent
          </button>
        </div>
      </header>

      {error && <div className="agents-page__error">{error}</div>}

      <section className="agents-panel agents-panel--management">
        <div className="agents-panel__header">
          <div>
            <h2>Agent Management</h2>
            <p>
              Select an agent, then open results or activity from the card
              actions on the right.
            </p>
          </div>
        </div>

        <div className="agents-management-toolbar">
          <div className="agents-management-toolbar__controls">
            <label className="agents-management-toolbar__field">
              <span>Search Agent Name</span>
              <input
                type="search"
                value={agentSearch}
                onChange={(event) => setAgentSearch(event.target.value)}
                placeholder="Search by agent name"
                aria-label="Search agent name"
              />
            </label>

            <label className="agents-management-toolbar__field agents-management-toolbar__field--sort">
              <span>Sort Agents</span>
              <select
                value={agentSort}
                onChange={(event) =>
                  setAgentSort(event.target.value as AgentSortOption)
                }
                aria-label="Sort agents"
              >
                <option value="default">Default order</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="next-run-soonest">Next run soonest</option>
                <option value="last-run-recent">Last run most recent</option>
              </select>
            </label>
          </div>

          <div className="agents-management-toolbar__meta">
            <div className="agents-management-toolbar__count" aria-live="polite">
              {filteredAgents.length === 0
                ? `Showing 0 of ${agents.length} agents`
                : totalPages > 1
                  ? `Showing ${startIndex + 1}-${endIndex} of ${filteredAgents.length}${hasAgentSearch ? ` matching ${agents.length}` : ""} agents`
                  : `Showing ${filteredAgents.length} of ${agents.length} agents`}
            </div>

            {totalPages > 1 && (
              <div className="agents-pagination">
                <div className="agents-pagination__controls">
                  <button
                    type="button"
                    className="agents-pagination__btn"
                    onClick={() =>
                      setCurrentPage((previousPage) => Math.max(1, previousPage - 1))
                    }
                    disabled={currentPage === 1}
                    title="Previous page"
                  >
                    ← Previous
                  </button>
                  <div className="agents-pagination__numbers">
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                      (pageNumber) => {
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 &&
                            pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNumber}
                              type="button"
                              className={`agents-pagination__number ${
                                currentPage === pageNumber
                                  ? "agents-pagination__number--active"
                                  : ""
                              }`}
                              onClick={() => setCurrentPage(pageNumber)}
                              aria-label={`Page ${pageNumber}`}
                              aria-current={
                                currentPage === pageNumber ? "page" : undefined
                              }
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return (
                            <span
                              key={pageNumber}
                              className="agents-pagination__ellipsis"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      },
                    )}
                  </div>
                  <button
                    type="button"
                    className="agents-pagination__btn"
                    onClick={() =>
                      setCurrentPage((previousPage) =>
                        Math.min(totalPages, previousPage + 1),
                      )
                    }
                    disabled={currentPage === totalPages}
                    title="Next page"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="agents-empty">Loading agents...</div>
        ) : agents.length === 0 ? (
          <div className="agents-empty">
            No agents yet. Create one to start scheduled search automation.
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="agents-empty">
            No agents match that name. Try a different search or clear it.
          </div>
        ) : (
          <div className="agents-list" data-tour-id="agents-list">
            {paginatedAgents.map((agent) => {
              const isSelected = agent.agent_id === selectedAgentId;
              const isPolling = pollingRun?.agentId === agent.agent_id;

              return (
                <article
                  key={agent.agent_id}
                  className={`agents-card ${isSelected ? "agents-card--selected" : ""}`}
                  onClick={() => setSelectedAgentId(agent.agent_id)}
                >
                  <div className="agents-card__main">
                    <div className="agents-card__header">
                      <div>
                        <h3>{agent.name}</h3>
                        <p>{agent.query}</p>
                      </div>
                      <span
                        className={`agents-status agents-status--${agent.last_run_status || "idle"}`}
                      >
                        {describeRunStatus(agent.last_run_status)}
                      </span>
                    </div>

                    <div className="agents-card__meta">
                      <span>{agent.source_ids.length} sources</span>
                      <span>Every {agent.interval_weeks} week(s)</span>
                      <span>{agent.is_active ? "Active" : "Paused"}</span>
                    </div>
                  </div>

                  <div className="agents-card__timing">
                    <div>
                      <span>Next run</span>
                      <strong>{formatTimestamp(agent.next_run_at)}</strong>
                    </div>
                    <div>
                      <span>Last run</span>
                      <strong>{formatTimestamp(agent.last_run_at)}</strong>
                    </div>
                  </div>

                  <div
                    className="agents-card__actions"
                    data-tour-id={isSelected ? "agents-card-actions" : undefined}
                  >
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        openDetailPanel(agent, "results");
                      }}
                    >
                      Results
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        openDetailPanel(agent, "runs");
                      }}
                    >
                      Runs
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        startEditing(agent);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleToggleActive(agent);
                      }}
                    >
                      {agent.is_active ? "Pause" : "Activate"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleRunNow(agent);
                      }}
                      disabled={isPolling}
                    >
                      {isPolling ? "Queued..." : "Run Now"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDelete(agent);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AgentsPage;
